/* ==========================================================================
   <cheo-accordion> — apertura y cierre animados (Calzado Cheo)

   Envuelve un <details> y le añade la transición de 240 ms que pide el diseño
   (design/README.md §10: 150–250 ms ease-out). El <details> sigue siendo el
   elemento real: sin JavaScript abre y cierra igual, el contenido sigue siendo
   indexable y el teclado funciona sin que añadamos nada.

   ¿Por qué JS y no CSS? Porque animar `<details>` con CSS depende de
   `::details-content` + `interpolate-size`, que todavía no está en todos los
   navegadores que usa la tienda. La Web Animations API sí, y el coste son 60
   líneas sin dependencias.

   Con `prefers-reduced-motion` no intercepta el clic: deja que el navegador abra
   y cierre de golpe, que es exactamente lo que el usuario pidió.
   ========================================================================== */

class CheoAccordion extends HTMLElement {
  static DURATION = 240;

  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.content = this.querySelector('[data-accordion-content]');
    if (!this.details || !this.summary || !this.content) return;

    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.summary.addEventListener('click', this.onSummaryClick);
  }

  disconnectedCallback() {
    this.summary?.removeEventListener('click', this.onSummaryClick);
    this.animation?.cancel();
    this.animation = null;
  }

  get reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  onSummaryClick(event) {
    if (this.reduced) return;

    event.preventDefault();

    if (this.details.open) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  /* Altura visible ahora mismo. Se mide ANTES de cancelar la animación en curso:
     `cancel()` devuelve el elemento a su estado sin animar y la medida se
     perdería, y el acordeón daría un salto al cambiar de dirección a medio
     camino. */
  currentHeight() {
    return this.animation ? this.content.getBoundingClientRect().height : 0;
  }

  expand() {
    const from = this.currentHeight();
    this.animation?.cancel();

    /* Hay que abrirlo para poder medirlo: con el <details> cerrado el contenido
       no está maquetado y scrollHeight vale 0. */
    this.details.open = true;
    this.dataset.open = 'true';

    this.run(from, this.content.scrollHeight);
  }

  collapse() {
    const from = this.currentHeight() || this.content.scrollHeight;
    this.animation?.cancel();
    this.dataset.open = 'false';

    this.run(from, 0).then((finished) => {
      if (finished) this.details.open = false;
    });
  }

  /* `height` de 0 a scrollHeight funciona porque la sección hereda
     `box-sizing: border-box`: con height 0 el padding también se colapsa, así que
     no hace falta animarlo aparte ni envolver el contenido en otro div. */
  async run(from, to) {
    const animation = this.content.animate(
      {
        height: [`${from}px`, `${to}px`],
        opacity: [from === 0 ? 0 : 1, to === 0 ? 0 : 1],
      },
      { duration: CheoAccordion.DURATION, easing: 'ease-out' }
    );

    this.animation = animation;
    this.classList.add('is-animating');

    try {
      await animation.finished;
    } catch {
      /* cancel() rechaza la promesa: otra interacción tomó el relevo y es ella
         la que decide el estado final. */
      return false;
    }

    if (this.animation === animation) {
      this.animation = null;
      this.classList.remove('is-animating');
    }

    return true;
  }
}

if (!customElements.get('cheo-accordion')) {
  customElements.define('cheo-accordion', CheoAccordion);
}

/* Editor de temas: al seleccionar el bloque, ábrelo. Sin esto el merchant hace
   clic en «Descripción» en la barra lateral y no ve cambiar nada. */
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:block:select', (event) => {
    const accordion = event.target.closest('cheo-accordion');
    /* `details` solo existe si el elemento ya se actualizó: sin esa comprobación
       expand() reventaría al seleccionar un bloque recién insertado. */
    if (accordion?.details && !accordion.details.open) accordion.expand();
  });
}
